import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Score Wheel (300-850) ── */
const ScoreWheel = ({ score, size = 180 }) => {
  const min = 300, max = 850, s = Math.max(min, Math.min(max, score || min));
  const pct = ((s - min) / (max - min)) * 100;
  const r = size * 0.38, cx = size / 2, cy = size * 0.52;
  const arc = 2 * Math.PI * r * 0.75;
  const offset = arc - (arc * pct) / 100;
  const label = s >= 800 ? 'Exceptional' : s >= 740 ? 'Very Good' : s >= 670 ? 'Good' : s >= 580 ? 'Fair' : 'Poor';
  const clr = s >= 740 ? '#16a34a' : s >= 670 ? '#22c55e' : s >= 580 ? '#eab308' : '#ef4444';
  return (
    <div className="ap-wheel" data-testid="score-wheel">
      <svg width={size} height={size * 0.58} viewBox={`0 0 ${size} ${size * 0.58}`}>
        <defs>
          <linearGradient id="wg" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" /><stop offset="25%" stopColor="#f97316" />
            <stop offset="45%" stopColor="#eab308" /><stop offset="70%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="#16a34a" />
          </linearGradient>
        </defs>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={12}
          strokeDasharray={`${arc} ${2*Math.PI*r}`} strokeLinecap="round" transform={`rotate(135 ${cx} ${cy})`} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#wg)" strokeWidth={12}
          strokeDasharray={`${arc} ${2*Math.PI*r}`} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(135 ${cx} ${cy})`} style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div className="ap-wheel-inner">
        <span className="ap-wheel-score" style={{ color: clr }}>{s}</span>
        <span className="ap-wheel-label">{label}</span>
      </div>
    </div>
  );
};

/* ── Utilization Progress Bar ── */
const UtilProgress = ({ before, after }) => {
  const b = Math.min(parseFloat(before) || 0, 200);
  const a = Math.min(parseFloat(after) || 0, 100);
  const bW = Math.min(b, 100);
  const bClr = b > 75 ? '#ef4444' : b > 50 ? '#f97316' : b > 30 ? '#eab308' : '#22c55e';
  const aClr = a > 30 ? '#eab308' : '#22c55e';
  return (
    <div className="ap-util-prog">
      <div className="ap-util-track">
        <div className="ap-util-fill-before" style={{ width: `${bW}%`, background: bClr }} />
        <div className="ap-util-fill-after" style={{ width: `${a}%`, background: aClr }} />
      </div>
      <div className="ap-util-labels">
        <span className="ap-util-before-text" style={{ color: bClr }}>{Math.round(b)}%</span>
        <span className="ap-util-arrow">&rarr;</span>
        <span className="ap-util-after-text" style={{ color: aClr }}>{Math.round(a)}%</span>
      </div>
    </div>
  );
};

const ActionPlanTab = ({ plan }) => {
  const [completedActions, setCompletedActions] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [modal, setModal] = useState(null);
  const [animIdx, setAnimIdx] = useState(null);

  const sid = localStorage.getItem('creditSessionId');
  const userName = localStorage.getItem('userName') || plan?.user_name || '';
  const name = userName || 'there';

  if (!plan) return <div className="ap-wrap"><div className="ap-loading"><div className="spinner" /><p>Preparing your action plan...</p></div></div>;

  // ═══ SINGLE RENDER SOURCE: final_plan.ui_sections ═══
  const fp = plan.final_plan || {};
  const ui = fp.ui_sections || {};
  const hero = ui.hero || {};

  // All display values come from ui_sections ONLY — no recalculation
  const displayScore = hero.current_score || fp.current_score || plan.current_score || 620;
  const gs = hero.goal_score || fp.goal_score || 720;
  const tm = hero.timeline_months || fp.timeline_months || 6;
  const mb = fp.monthly_budget || plan.monthly_budget || 300;
  const gain = hero.score_gain || (gs - displayScore);
  const scoreLabel = hero.score_label || fp.score_label || 'Fair';
  const scoreNote = hero.score_note || fp.score_note || null;
  const dq = hero.confidence || fp.confidence || 'medium';
  const confLabel = hero.confidence_label || (dq === 'high' ? 'High' : dq === 'medium' ? 'Medium' : 'Low');
  const pdfReady = plan.pdf_status === 'ready';

  // Pre-resolved display sections
  const primaryCards = ui.primary_action_cards || [];
  const stretchCards = ui.stretch_moves_display || [];
  const budgetAlloc = ui.budget_allocation_display || {};
  const budgetRows = budgetAlloc.rows || [];
  const optionalPayments = ui.optional_payments_display || {};
  const statsRow = ui.stats || [];
  const budgetNote = ui.budget_note || fp.budget_note || '';
  const contextLabel = ui.context_label || 'Your Fastest Score Boost Plan (Based on Your Current Budget)';
  const trustMicrocopy = ui.trust_microcopy || '';
  const proTips = ui.pro_tips || [];

  // Fallback to raw plan data if ui_sections not yet populated (old plans)
  const acts = primaryCards.length > 0 ? primaryCards : (plan.actions || []).map((a, i) => ({
    step_number: i + 1,
    priority_label: a.priority_label || 'PRIMARY BUDGET PLAN',
    account_name: a.account?.name || 'Account',
    last4: a.account?.last4 || 'XXXX',
    payment_amount: a.account?.payment_amount || 0,
    current_utilization: a.account?.current_utilization || 0,
    target_utilization: a.account?.target_utilization || 0,
    impact_display: a.score_simulation?.estimated_gain || '+10-20 pts (estimated)',
    why: a.why_this_account_first || '',
    command: a.command || '',
  }));
  const stretchMoves = stretchCards.length > 0 ? stretchCards : (plan.stretch_moves || []).map((s, i) => ({
    move_number: i + 1,
    account_name: s.account_name || 'Account',
    last4: s.last4 || 'XXXX',
    amount: s.amount || 0,
    current_utilization: s.current_utilization || 0,
    target_utilization: s.target_utilization || 0,
    impact_display: s.estimated_impact || '+5-10 pts (estimated)',
    reason: s.reason || '',
  }));

  const done = completedActions.length;
  const nextIdx = acts.findIndex((_, i) => !completedActions.includes(i));
  const pct = acts.length > 0 ? Math.round((done / acts.length) * 100) : 0;

  const execute = (i) => {
    if (completedActions.includes(i)) { setCompletedActions(completedActions.filter(x => x !== i)); return; }
    setAnimIdx(i);
    setTimeout(() => {
      setCompletedActions(prev => [...prev, i]);
      setModal({ gain: acts[i]?.score_simulation?.change || acts[i]?.score_simulation?.estimated_gain || '+15 pts', msg: acts[i]?.completion_message || `Great work, ${name}!`, next: acts[i + 1] });
      setAnimIdx(null);
    }, 400);
  };

  // ═══ DOWNLOAD: BLOB-BASED (WORKS CROSS-ORIGIN + MOBILE) ═══
  const download = async () => {
    if (!sid) return;
    setDownloading(true);
    const url = `${API}/plan/${sid}/download`;
    
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'Fast-Track-Score-Boost-Plan.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        setDownloading(false);
        return;
      } catch (err) {
        console.error(`Download attempt ${attempt} failed:`, err);
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
      }
    }
    // Final fallback: open in new tab
    try { window.open(url, '_blank'); } catch {}
    setDownloading(false);
  };

  return (
    <div className="ap-wrap" data-testid="action-plan">

      {/* ═══ HEADER ═══ */}
      <div className="ap-header">
        <h1 className="ap-title">{name}'s <span>Fast-Track Score Boost Plan</span></h1>
        <p className="ap-subtitle">Your fastest path to increasing your credit score based on your current profile.</p>
      </div>

      {/* ═══ HERO: SCORE CARD ═══ */}
      <div className="ap-hero" data-testid="hero-card">
        <div className="ap-hero-left">
          <p className="ap-hero-label">Current Score</p>
          <div className="ap-hero-scores">
            <span className="ap-hero-current">{displayScore}</span>
            <span className="ap-hero-arrow">&rarr;</span>
            <span className="ap-hero-goal">{gs}</span>
          </div>
          <p className="ap-hero-gain">+{gain} pts in {tm} months</p>
          {scoreNote && <p className="ap-hero-note">{scoreNote}</p>}
          <span className={`ap-confidence ap-conf-${dq}`}>Plan Confidence: <strong>{confLabel}</strong></span>
        </div>
        <ScoreWheel score={displayScore} size={160} />
      </div>

      {/* Stats row — from ui_sections.stats */}
      <div className="ap-stats">
        {statsRow.length > 0 ? statsRow.map((s, i) => (
          <div key={i} className="ap-stat"><span className="ap-stat-label">{s.label}</span><span className="ap-stat-value">{s.value}</span></div>
        )) : (
          [['Monthly Budget', `$${mb}`], ['Timeline', `${tm} months`], ['Score', scoreLabel]].map(([l, v], i) => (
            <div key={i} className="ap-stat"><span className="ap-stat-label">{l}</span><span className="ap-stat-value">{v}</span></div>
          ))
        )}
      </div>

      {/* ═══ DO THIS FIRST — from first ui_sections card ═══ */}
      {acts.length > 0 && acts[0].account_name && acts[0].account_name !== 'Account' && (
        <div className="ap-first" data-testid="do-this-first">
          <div className="ap-first-header">
            <span className="ap-first-icon">&#x1F680;</span>
            <span className="ap-first-title">Do This First</span>
            <span className="ap-first-tag">&#x1F525; Fastest Score Boost</span>
          </div>
          <div className="ap-first-body">
            <div className="ap-first-action">
              <span className="ap-first-pay">Pay ${acts[0].payment_amount || mb}</span>
              <span className="ap-first-acct">&rarr; {acts[0].account_name} ••{acts[0].last4}</span>
            </div>
            {acts[0].current_utilization > 0 && (
              <>
                <div className="ap-first-util">
                  <span>Utilization: {Math.round(acts[0].current_utilization)}% &rarr; {Math.round(acts[0].target_utilization)}%</span>
                </div>
                <UtilProgress before={acts[0].current_utilization} after={acts[0].target_utilization} />
              </>
            )}
            <p className="ap-first-note">This one move alone can unlock your biggest score increase.</p>
          </div>
          {!completedActions.includes(0) && (
            <button className="ap-first-btn" onClick={() => execute(0)} data-testid="execute-first">
              Execute Now — Unlock {acts[0].impact_display}
            </button>
          )}
        </div>
      )}

      {/* ═══ UTILIZATION INSIGHT ═══ */}
      {acts.length > 0 && acts[0].current_utilization > 30 && (
        <div className="ap-insight" data-testid="util-insight">
          <p>Your <strong>{acts[0].account_name}</strong> account is significantly over the optimal utilization range. Reducing it below 30% is one of the fastest ways to increase your score.</p>
        </div>
      )}

      {/* ═══ PROGRESS ═══ */}
      <div className="ap-progress">
        <div className="ap-prog-head"><span>{done} of {acts.length} actions</span><span className="ap-prog-pct">{pct}%</span></div>
        <div className="ap-prog-bar"><div className="ap-prog-fill" style={{ width: `${pct}%` }} /></div>
      </div>

      {/* ═══ CONTEXT LABEL ═══ */}
      <div className="ap-context-label" data-testid="context-label">
        {contextLabel}
      </div>

      {/* Budget explanation */}
      {budgetNote && (
        <p className="ap-budget-note" data-testid="budget-note">{budgetNote}</p>
      )}

      {/* ═══ PRIMARY BUDGET PLAN — renders from ui_sections.primary_action_cards ═══ */}
      <h2 className="ap-section-title" data-testid="actions-title">Primary Budget Plan</h2>

      <div className="ap-cards">
        {acts.map((card, idx) => {
          const isDone = completedActions.includes(idx);
          const isCurr = idx === nextIdx;
          const ub = parseFloat(card.current_utilization) || 0;
          const ua = parseFloat(card.target_utilization) || 0;

          return (
            <div key={idx} className={`ap-card ${isDone ? 'ap-card-done' : ''} ${isCurr ? 'ap-card-active' : ''} ${animIdx === idx ? 'ap-card-anim' : ''}`} data-testid={`action-card-${idx}`}>
              <div className="ap-card-top">
                <div className="ap-card-step">
                  {isDone ? <span className="ap-card-check">&#10003;</span> : (
                    <>{card.priority_label || 'PRIMARY BUDGET PLAN'} — Step {card.step_number || idx + 1}</>
                  )}
                </div>
                {isCurr && !isDone && <span className="ap-card-next">NEXT</span>}
                <div className="ap-card-tags">
                  {ub > 70 && <span className="ap-tag ap-tag-boost">&#x26A1; Fastest Boost</span>}
                  <span className="ap-tag ap-tag-impact">&#x1F525; High Impact</span>
                </div>
              </div>
              <div className="ap-card-body">
                <div className="ap-card-head">
                  <div><p className="ap-card-name">{card.account_name}</p><p className="ap-card-num">••{card.last4}</p></div>
                  <span className="ap-card-amount">${card.payment_amount || '—'}</span>
                </div>
                {ub > 0 && <UtilProgress before={ub} after={ua} />}
                <div className="ap-card-impact">Estimated Impact: <strong>{card.impact_display}</strong></div>
                {card.why && <p className="ap-card-why">{card.why}</p>}
              </div>
              {!isDone && (
                <button className={`ap-card-btn ${isCurr ? 'ap-card-btn-go' : ''}`} onClick={() => execute(idx)} data-testid={`execute-action-${idx}`}>
                  {isCurr ? 'Execute Now' : 'Mark Complete'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ═══ STRETCH MOVES — ALWAYS RENDERS (from ui_sections.stretch_moves_display) ═══ */}
      <h2 className="ap-section-title ap-stretch-section-title" data-testid="stretch-title">Optional Stretch Payments</h2>
      {stretchMoves.length > 0 ? (
        <>
          <p className="ap-stretch-desc">Your current budget has been fully allocated to the highest-impact move. If you can pay extra this month, these are the next best accounts to target.</p>
          <div className="ap-cards">
            {stretchMoves.map((sm, i) => (
              <div key={i} className="ap-card ap-card-stretch" data-testid={`stretch-move-${i}`}>
                <div className="ap-card-top ap-card-top-stretch">
                  <div className="ap-card-step">OPTIONAL STRETCH — MOVE {sm.move_number || i + 1}</div>
                  <div className="ap-card-tags">
                    <span className="ap-tag ap-tag-stretch">Optional</span>
                    <span className="ap-tag ap-tag-impact">&#x1F525; High Impact</span>
                  </div>
                </div>
                <div className="ap-card-body">
                  <div className="ap-card-head">
                    <div><p className="ap-card-name">{sm.account_name}</p><p className="ap-card-num">••{sm.last4}</p></div>
                    <span className="ap-card-amount ap-card-amount-stretch">+${sm.amount}</span>
                  </div>
                  {sm.current_utilization > 0 && <UtilProgress before={sm.current_utilization} after={sm.target_utilization} />}
                  <div className="ap-card-impact">Potential Impact: <strong>{sm.impact_display}</strong></div>
                  {sm.reason && <p className="ap-card-why">{sm.reason}</p>}
                </div>
                <button className="ap-card-btn ap-card-btn-stretch" data-testid={`stretch-execute-${i}`}>
                  EXECUTE NOW
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="ap-stretch-empty" data-testid="stretch-empty">No additional high-impact stretch moves found from this report.</p>
      )}

      {/* ═══ TRUST MICROCOPY ═══ */}
      <div className="ap-trust-micro" data-testid="trust-microcopy">
        {trustMicrocopy || 'This plan is designed to maximize your score improvement within your current budget and timeline.'}
      </div>

      {/* ═══ BUDGET ALLOCATION — from ui_sections.budget_allocation_display ═══ */}
      {budgetRows.length > 0 && (
        <div className="ap-budget" data-testid="budget-section">
          <h3 className="ap-section-title">{budgetAlloc.title || `Monthly Budget Allocation — $${mb}`}</h3>
          {budgetRows.map((row, i) => (
            <div key={i} className="ap-budget-row">
              <div className="ap-budget-info">
                <span className="ap-budget-name">{row.label}</span>
              </div>
              <div className="ap-budget-track"><div className="ap-budget-fill" style={{ width: `${row.pct_width}%` }} /></div>
            </div>
          ))}
          {optionalPayments.has_items && (
            <div className="ap-budget-stretch-label">
              <span className="ap-budget-stretch-title">Optional Stretch Payments:</span>
              {(optionalPayments.rows || []).map((op, i) => (
                <span key={i} className="ap-budget-stretch-item">{op.label}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ PRO TIPS — from ui_sections.pro_tips ═══ */}
      {proTips.length > 0 && (
        <div className="ap-pro-tips" data-testid="pro-tips">
          <h3 className="ap-section-title">Pro Tips</h3>
          {proTips.map((tip, i) => (
            <p key={i} className="ap-pro-tip">{tip}</p>
          ))}
        </div>
      )}

      {/* ═══ DOWNLOAD ═══ */}
      <button className="ap-download" onClick={download} disabled={downloading || !pdfReady} data-testid="download-plan-btn">
        {downloading ? 'Downloading...' : !pdfReady ? 'Preparing PDF...' : 'Download Action Plan (PDF)'}
      </button>

      {/* ═══ CHECKLIST ═══ */}
      <div className="ap-checklist" data-testid="checklist">
        <h3 className="ap-section-title">Execution Checklist</h3>
        {['Log into each credit card account listed in this plan',
          'Note your statement closing dates',
          'Set reminders 5-7 days before each closing date',
          'Make payments in order — Step 1 first',
          'Pay at least the amounts listed',
          'Verify new balance after payment posts',
          'Check credit score 2-3 weeks after statement close'
        ].map((item, i) => (
          <div key={i} className="ap-check-item"><span className="ap-check-box">&#x2610;</span><span>{item}</span></div>
        ))}
      </div>

      {/* ═══ TRUST ═══ */}
      <div className="ap-trust" data-testid="trust-section">
        <h3 className="ap-trust-title">Why These Actions Were Selected</h3>
        <p>This plan focuses on the fastest ways to improve your credit score based on your current profile.</p>
        <p>We prioritize actions that can create immediate score impact, such as reducing high credit utilization.</p>
        <p>Some accounts — like installment loans, older accounts, or certain negative items — may not be included because they do not produce immediate score changes.</p>
      </div>

      {/* ═══ PRO TIPS ═══ */}
      <div className="ap-tips" data-testid="pro-tips">
        <h3 className="ap-section-title">Pro Tips</h3>
        <div className="ap-tip-item">Don't close old credit cards — age of credit matters</div>
        <div className="ap-tip-item">Avoid opening new credit accounts while executing this plan</div>
        <div className="ap-tip-item">If you can't afford the full payment, pay what you can — partial progress is still progress</div>
        <div className="ap-tip-item">Keep utilization below 30% on ALL cards, not just the ones in this plan</div>
      </div>

      {/* ═══ DISCLAIMER ═══ */}
      <div className="ap-disclaimer" data-testid="disclaimer">
        This plan is personalized guidance based on your credit report data, designed to give you the fastest measurable results first. Results may vary based on creditor reporting and other factors.
      </div>

      {/* ═══ MODAL ═══ */}
      {modal && (
        <div className="ap-modal-bg" onClick={() => setModal(null)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-party">&#127881;</div>
            <h3>Nice work{userName ? `, ${userName}` : ''}!</h3>
            <div className="ap-modal-gain"><span>{modal.gain}</span><small>ESTIMATED IMPACT</small></div>
            <p>{modal.msg}</p>
            {modal.next && <div className="ap-modal-next"><small>NEXT MOVE:</small><p>{modal.next.command || modal.next.title}</p></div>}
            <button onClick={() => setModal(null)}>Continue</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionPlanTab;
